'use client';

import React from 'react';
import { useForm } from 'react-hook-form';
import { ProfileFormData } from './CreateProfileWizard';

interface Props {
  form: ReturnType<typeof useForm<ProfileFormData>>;
}

const socialFields = [
  {
    key: 'instagram' as keyof ProfileFormData,
    label: 'Instagram',
    icon: '📸',
    prefix: 'instagram.com/',
    placeholder: 'yourhandle',
    color: 'bg-pink-50 border-pink-100',
  },
  {
    key: 'youtube' as keyof ProfileFormData,
    label: 'YouTube',
    icon: '▶',
    prefix: 'youtube.com/',
    placeholder: '@YourChannel',
    color: 'bg-red-50 border-red-100',
  },
  {
    key: 'twitter' as keyof ProfileFormData,
    label: 'Twitter / X',
    icon: '🐦',
    prefix: 'x.com/',
    placeholder: 'yourhandle',
    color: 'bg-sky-50 border-sky-100',
  },
  {
    key: 'website' as keyof ProfileFormData,
    label: 'Website',
    icon: '🌐',
    prefix: 'https://',
    placeholder: 'yourwebsite.com',
    color: 'bg-violet-50 border-violet-100',
  },
];

export default function ProfileStep2({ form }: Props) {
  const { register } = form;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-700 text-foreground mb-1">Social Profiles</h2>
        <p className="text-sm text-muted-foreground">
          Connect your social accounts. These appear as icons on your bio page.
          All fields are optional.
        </p>
      </div>

      <div className="space-y-4">
        {socialFields.map((field) => (
          <div key={`social-field-${field.key}`}>
            <label
              htmlFor={`social-${field.key}`}
              className="flex items-center gap-2 text-sm font-600 text-foreground mb-1.5"
            >
              <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-base border ${field.color}`}>
                {field.icon}
              </span>
              {field.label}
              <span className="text-xs text-muted-foreground font-400">— optional</span>
            </label>
            <div className="relative flex items-center border border-border rounded-xl bg-background overflow-hidden focus-within:ring-2 focus-within:ring-primary/40 transition-all">
              <span className="pl-4 pr-2 py-3 text-sm text-muted-foreground bg-muted border-r border-border whitespace-nowrap flex-shrink-0">
                {field.prefix}
              </span>
              <input
                id={`social-${field.key}`}
                type="text"
                placeholder={field.placeholder}
                className="flex-1 px-3 py-3 text-sm text-foreground placeholder:text-muted-foreground bg-transparent focus:outline-none"
                {...register(field.key)}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="bg-primary/5 border border-primary/20 rounded-xl p-4">
        <p className="text-xs font-600 text-primary mb-1 flex items-center gap-1.5">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Pro tip
        </p>
        <p className="text-xs text-muted-foreground">
          You can add more links (Spotify, WhatsApp, custom URLs) from your dashboard after publishing.
        </p>
      </div>
    </div>
  );
}